import { UserProfile } from "@models/userProfile.model"
import { UserInfo } from "./oauthService"

// Security: Validation and sanitization functions to prevent privilege escalation (CWE-522)
const validateAndSanitizeString = (
	value: unknown,
	defaultValue = ""
): string => {
	// Ensure strings are actually strings and sanitize to prevent injection
	if (typeof value === "string") {
		// Remove potentially dangerous characters but allow most normal characters
		return value.trim()
	}
	return defaultValue
}

export const adaptForgeRockUser = (userInfo: UserInfo): UserProfile => {
	// Security: Validate and sanitize all user input to prevent privilege escalation
	// Never trust default values from OAuth userInfo - always validate and sanitize

	// Sanitize basic string fields
	const loginUserName = validateAndSanitizeString(
		userInfo.sub || userInfo.preferred_username
	)
	const agentFirstName = validateAndSanitizeString(userInfo.given_name)
	const agentLastName = validateAndSanitizeString(userInfo.family_name)
	const emailAddress = validateAndSanitizeString(userInfo.email)

	// Security: Critical fields must be validated - default values should not grant privileges
	// All IDs default to 0 (invalid) until validated by backend
	// All permission flags default to false (no access) until validated by backend
	// Role codes default to empty (no role) until validated by backend

	return {
		loginUserName,
		agentFirstName,
		agentLastName,
		emailAddress,
		agentEmailAddress: emailAddress,

		// --- Validated and sanitized default values for required fields ---
		// Security: These defaults provide NO privileges (CWE-522)
		// Backend API must validate and populate these after authentication
		agentId: 0, // Invalid ID - must be validated by backend
		allowedApplicationTools: [], // No tools granted by default
		headquarterGroupSailingsAvailable: false, // No privileges by default
		isHeadquarter: false, // Not headquarter by default
		dismissedNotifications: [], // Empty array
		agencyId: 0, // Invalid ID - must be validated by backend
		agencyName: "",
		urlAddress: "",
		homeBasedAgent: false,
		languageCode: validateAndSanitizeString(userInfo.locale, "en-US"),
		localeCountryCode: "US", // Default, consider deriving from `locale`
		countryCode: "US", // Default, consider deriving from `locale`
		reportingOfficeCode: "",
		headQuarterAgencyId: null,
		registrationDate: new Date().toISOString(),
		colonialUserId: null,
		internationalRepFlag: false,
		roleCode: "", // Security: Empty role by default - backend must assign privileges
		agencyCategoryType: null,
		eQuoteAcceptanceTC: false,
		currencies: [
			// Default currency structure - should be validated by backend
			{
				agencyId: 0, // Invalid ID - must be validated
				currencyCode: "USD",
				primaryCurrency: true
			}
		],
		primaryCurrency: "USD",
		// Security: All access flags default to false - no privileges granted
		hasFlexPayAccess: false,
		hasEquoteAccess: false,
		agentPhotos: {
			AGENT_HOMEPAGE_PHOTO: {
				uri: "",
				defaultFlag: true,
				pixelHeight: 0,
				pixelWidth: 0,
				fullPath: ""
			}
		},
		locationData: [],
		latteAvailable: false,
		lat: "",
		long: "",
		agencyPhone: "",
		agentCountryCode: "US",
		reAcceptTerms: false
	}
}
